/**
 * PerformanceAnalyticsView - Layout for player performance analytics
 *
 * Displays spider graph, distribution charts, and dot plot for player metrics
 * compared against position averages. The shared comparison legend
 * (player dot, compare player dot, average dot) is extracted into the
 * ComparisonLegend component to avoid duplication between panels.
 * The left panel exposes an onLayout callback so the container can
 * measure available width and derive responsive chart dimensions.
 * On mobile: vertically stacked cards (Spider Graph, Distribution, Dot Plot)
 * with compact distribution rows. DotPlot is rendered directly in the View
 * to avoid Victory SVG painting issues with pre-memoized ReactNodes.
 * On desktop: two independent CSS Grid rows (`grid grid-cols-2 gap-4`) inside
 * a VStack. Top row: Spider Graph + Distribution. Bottom row: Dot Plot +
 * Metric Insights. Each row sizes to its tallest cell independently, so the
 * bottom row can be shorter than the top row.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import React from "react";
import type { LayoutChangeEvent } from "react-native";
// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: left panel needs onLayout width measurement and the distribution rows use the RN-only removeClippedSubviews prop +…
import { Platform, View } from "react-native";

import ComparisonLegend from "@/features/player-detail/components/organisms/ComparisonLegend";
import DotPlot from "@/features/player-detail/components/molecules/DotPlot";
import MetricDistributionRow from "@/features/player-detail/components/molecules/MetricDistributionRow";
import {
  renderDesktopHeader,
  renderDesktopSubmenu,
  renderMobileHeader,
  renderMobileSubmenu,
  type ShellDropdownWiring,
} from "@/features/player-detail/utils/performanceAnalyticsViewHelpers";
import type { PerformanceShellTriggerProps } from "@/features/player-detail/hooks/usePerformanceMenuTriggers";
import type {
  CompareItem,
  DistributionComparisonStatus,
  MetricOption,
  TimeframeOption,
} from "@/features/player-detail/types";
import type { BeeSwarmInput } from "@/features/player-detail/utils/beeSwarmPositioning";
import type { MetricDescription } from "@/features/player-detail/constants";
import ScopedModal from "@/components/organisms/shared/ScopedModal";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/**
 * Metric data for distribution charts with player info for tooltips
 */
interface MetricData {
  readonly label: string;
  readonly value: number | null;
  readonly distributionData: readonly BeeSwarmInput[];
}

/**
 * Theme classes for the component
 */
interface ThemeClasses {
  readonly containerClass: string;
  readonly headingClass: string;
  readonly textClass: string;
}

/**
 * Player data point for scatter plot rendering in mobile DotPlot
 */
interface ScatterPlotPlayer {
  readonly id: string;
  readonly name: string;
  readonly x: number | null;
  readonly y: number | null;
  readonly isCurrentPlayer: boolean;
  readonly isComparisonPlayer?: boolean;
}

/**
 * Props for PerformanceAnalyticsView component
 */
interface PerformanceAnalyticsViewProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Whether the screen is mobile sized */
  readonly isSmallScreen: boolean;
  /** Content to render below the stacked cards on mobile and in the bottom-right grid cell on desktop (e.g. IndividualMetricInsights) */
  readonly additionalContent?: React.ReactNode;
  /** Dot plot chart size in px for mobile (ensures container has explicit dimensions) */
  readonly mobileDotPlotSize: number;
  /** Scatter plot player data points for mobile DotPlot rendering */
  readonly scatterPlotPlayers: readonly ScatterPlotPlayer[];
  /** X-axis metric for mobile DotPlot */
  readonly xMetricWithValue: MetricOption;
  /** Y-axis metric for mobile DotPlot */
  readonly yMetricWithValue: MetricOption;
  /** Render function for Y-axis menu trigger (mobile DotPlot, shell-hosted) */
  readonly renderYMenuTrigger: (
    props: PerformanceShellTriggerProps
  ) => React.ReactElement;
  /** Render function for X-axis menu trigger (mobile DotPlot, shell-hosted) */
  readonly renderXMenuTrigger: (
    props: PerformanceShellTriggerProps
  ) => React.ReactElement;
  /** Factory for creating Y-axis metric select handlers (mobile DotPlot) */
  readonly createYSelectHandler: (metric: MetricOption) => () => void;
  /** Factory for creating X-axis metric select handlers (mobile DotPlot) */
  readonly createXSelectHandler: (metric: MetricOption) => () => void;
  /** Shell wiring for the X-axis dropdown (mobile DotPlot, SE-5568) */
  readonly xAxisDropdown: ShellDropdownWiring;
  /** Shell wiring for the Y-axis dropdown (mobile DotPlot, SE-5568) */
  readonly yAxisDropdown: ShellDropdownWiring;
  /** Tooltip label formatter for mobile DotPlot */
  readonly dotPlotTooltipLabelFormatter: (args: {
    datum: { name: string; x: number | null; y: number | null };
  }) => string;
  /** Player name for legend */
  readonly playerName: string;
  /** Ref for spider chart container (web only, for PDF capture) */
  readonly spiderChartRef?: React.RefObject<HTMLDivElement | null>;
  /** Ref for dot plot container (web only, for PDF capture) */
  readonly dotPlotRef?: React.RefObject<HTMLDivElement | null>;
  /** Selected comparison item (null during initial load before player position is set) */
  readonly selectedCompareItem: CompareItem | null;
  /** Selected timeframe (null during initial load) */
  readonly selectedTimeframe: TimeframeOption | null;
  /** Radar chart content (passed from container) */
  readonly radarChartContent: React.ReactNode;
  /** Dot plot content (passed from container) */
  readonly dotPlotContent: React.ReactNode;
  /** Metrics data for distribution charts */
  readonly metrics: readonly MetricData[];
  /** What the Distribution card says about its comparison dots (SE-8283) */
  readonly distributionComparisonStatus: DistributionComparisonStatus;
  /** Theme classes computed by container */
  readonly theme: ThemeClasses;
  /** Available metrics for distribution selector */
  readonly availableMetrics: readonly MetricOption[];
  /** Handler for selecting a distribution metric at a specific index */
  readonly onSelectDistributionMetricAtIndex: (
    index: number,
    metric: MetricOption
  ) => void;
  /** Compare category options for dropdown */
  readonly compareCategoryOptions: readonly CompareItem[];
  /** Render function for Compare VS menu trigger */
  readonly renderCompareMenuTrigger: (
    props: PerformanceShellTriggerProps
  ) => React.JSX.Element;
  /** Factory for creating stable compare category select handlers */
  readonly createCompareCategorySelectHandler: (
    item: CompareItem
  ) => () => void;
  /** Shell open-state + positioning for the compare dropdown (SE-5551). */
  readonly compareDropdown: ShellDropdownWiring;
  /** Available timeframe options */
  readonly timeframeOptions: readonly TimeframeOption[];
  /** Render function for Timeframe menu trigger */
  readonly renderTimeframeMenuTrigger: (
    props: PerformanceShellTriggerProps
  ) => React.JSX.Element;
  /** Factory for creating stable timeframe select handlers */
  readonly createTimeframeSelectHandler: (
    timeframe: TimeframeOption
  ) => () => void;
  /** Shell open-state + positioning for the timeframe dropdown (SE-5551). */
  readonly timeframeDropdown: ShellDropdownWiring;
  /** Whether submenu is open */
  readonly isSubmenuOpen: boolean;
  /** Title for the submenu modal */
  readonly submenuTitle: string;
  /** Search placeholder for submenu */
  readonly submenuSearchPlaceholder: string;
  /** Search query for submenu */
  readonly submenuSearchQuery: string;
  /** Filtered items for submenu */
  readonly filteredSubmenuItems: readonly CompareItem[];
  /** Whether submenu search is loading */
  readonly isSubmenuLoading?: boolean;
  /** Whether the submenu has a further page of results to fetch (SE-7111) */
  readonly hasMoreSubmenuItems?: boolean;
  /** Whether the submenu's next page is currently in flight (SE-7111) */
  readonly isLoadingMoreSubmenuItems?: boolean;
  /** Fetches the submenu's next page of results (SE-7111) */
  readonly onLoadMoreSubmenuItems?: () => void;
  /** Whether distribution should update (only for shadow teams, watchlists, leagues) */
  readonly showComparisonDistribution: boolean;
  /** Whether comparing to a position (shows "Position Avg" legend) */
  readonly isPositionComparison: boolean;
  /** Whether comparing to a specific player (shows orange dot) */
  readonly isPlayerComparison: boolean;
  /** Currently selected compare category type (position, players, leagues, etc.) */
  readonly selectedCompareCategoryType: string;
  /** Comparison averages for distribution dots, null values indicate missing data */
  readonly comparisonAverages: Record<string, number | null>;
  /** Handler for closing submenu */
  readonly onCloseSubmenu: () => void;
  /** Handler for selecting submenu item */
  readonly onSelectSubmenuItem: (item: CompareItem) => void;
  /** Handler for submenu search change */
  readonly onSubmenuSearchChange: (query: string) => void;
  /** Whether info modal is open */
  readonly isInfoModalOpen: boolean;
  /** Handler for opening info modal */
  readonly onOpenInfoModal: () => void;
  /** Handler for closing info modal */
  readonly onCloseInfoModal: () => void;
  /** Metric descriptions for info modal */
  readonly metricDescriptions: readonly MetricDescription[];
  /** Layout handler for the left panel to measure available width for responsive chart sizing */
  readonly onLeftPanelLayout?: (event: LayoutChangeEvent) => void;
}

/** testID of the Distribution card comparison caption (SE-8283) */
export const DISTRIBUTION_COMPARISON_STATUS_TEST_ID =
  "distribution-comparison-status";

/** Caption copy per Distribution comparison status (SE-8283) */
export const DISTRIBUTION_COMPARISON_STATUS_COPY: Readonly<
  Record<Exclude<DistributionComparisonStatus, "hidden">, string>
> = {
  loading: "Loading comparison players…",
  error:
    "Comparison players could not be loaded. Reload the page to try again.",
  empty: "No comparison players with scores for this group.",
};

/**
 * Render the Distribution card caption about its comparison dots (SE-8283).
 *
 * Without it, a row waiting on the comparison request, a row whose request
 * failed, and a row whose group has no scores all show the player dot alone.
 * @param status - Distribution comparison status
 * @returns The caption, or null when the dots need no explanation
 */
const renderDistributionComparisonStatus = (
  status: DistributionComparisonStatus
): React.ReactNode =>
  status === "hidden" ? null : (
    <Text
      variant="caption"
      className={
        status === "error" ? "text-status-error" : "text-content-muted"
      }
      testID={DISTRIBUTION_COMPARISON_STATUS_TEST_ID}
    >
      {DISTRIBUTION_COMPARISON_STATUS_COPY[status]}
    </Text>
  );

/**
 * PerformanceAnalyticsView renders the performance analytics section
 * @param props - View props
 * @param props.isDark - Whether dark mode is active
 * @param props.isSmallScreen - Whether the screen is mobile sized
 * @param props.additionalContent - Content for the bottom of mobile stack and bottom-right grid cell on desktop
 * @param props.mobileDotPlotSize - Dot plot chart size for mobile layout
 * @param props.scatterPlotPlayers - Scatter plot player data points for mobile DotPlot
 * @param props.xMetricWithValue - X-axis metric for mobile DotPlot
 * @param props.yMetricWithValue - Y-axis metric for mobile DotPlot
 * @param props.renderYMenuTrigger - Render function for Y-axis menu trigger (mobile DotPlot)
 * @param props.renderXMenuTrigger - Render function for X-axis menu trigger (mobile DotPlot)
 * @param props.createYSelectHandler - Factory for Y-axis metric select handlers (mobile DotPlot)
 * @param props.createXSelectHandler - Factory for X-axis metric select handlers (mobile DotPlot)
 * @param props.xAxisDropdown - Shell wiring for the X-axis dropdown (mobile DotPlot, SE-5568)
 * @param props.yAxisDropdown - Shell wiring for the Y-axis dropdown (mobile DotPlot, SE-5568)
 * @param props.dotPlotTooltipLabelFormatter - Tooltip label formatter for mobile DotPlot
 * @param props.playerName - Player name for legend
 * @param props.spiderChartRef - Ref for spider chart container (web only, for PDF capture)
 * @param props.dotPlotRef - Ref for dot plot container (web only, for PDF capture)
 * @param props.selectedCompareItem - Selected comparison item
 * @param props.selectedTimeframe - Selected timeframe
 * @param props.radarChartContent - Radar chart content (passed from container)
 * @param props.dotPlotContent - Dot plot content (passed from container)
 * @param props.metrics - Metrics data for distribution charts
 * @param props.distributionComparisonStatus - What the Distribution card says about its comparison dots
 * @param props.theme - Theme classes computed by container
 * @param props.availableMetrics - Available metrics for distribution selector
 * @param props.onSelectDistributionMetricAtIndex - Handler for selecting distribution metric at index
 * @param props.compareCategoryOptions - Compare category options for dropdown
 * @param props.renderCompareMenuTrigger - Render function for Compare VS menu trigger
 * @param props.createCompareCategorySelectHandler - Factory for compare category handlers
 * @param props.compareDropdown - Shell open-state + positioning for the compare dropdown
 * @param props.timeframeOptions - Available timeframe options
 * @param props.renderTimeframeMenuTrigger - Render function for Timeframe menu trigger
 * @param props.createTimeframeSelectHandler - Factory for timeframe handlers
 * @param props.timeframeDropdown - Shell open-state + positioning for the timeframe dropdown
 * @param props.isSubmenuOpen - Whether submenu modal is open
 * @param props.submenuTitle - Title for the submenu modal
 * @param props.submenuSearchPlaceholder - Placeholder text for submenu search
 * @param props.submenuSearchQuery - Current search query in submenu
 * @param props.filteredSubmenuItems - Filtered items to display in submenu
 * @param props.isSubmenuLoading - Whether submenu search is loading
 * @param props.hasMoreSubmenuItems - Whether the submenu has a further page to fetch
 * @param props.isLoadingMoreSubmenuItems - Whether the submenu's next page is in flight
 * @param props.onLoadMoreSubmenuItems - Fetches the submenu's next page
 * @param props.showComparisonDistribution - Whether to show comparison distribution (shadow teams, watchlists, leagues only)
 * @param props.isPositionComparison - Whether comparing to a position (shows "Position Avg" legend)
 * @param props.isPlayerComparison - Whether comparing to a specific player (shows orange dot)
 * @param props.selectedCompareCategoryType - Currently selected compare category type
 * @param props.comparisonAverages - Comparison averages for distribution dots
 * @param props.onCloseSubmenu - Handler for closing submenu
 * @param props.onSelectSubmenuItem - Handler for selecting item from submenu
 * @param props.onSubmenuSearchChange - Handler for submenu search input change
 * @param props.isInfoModalOpen - Whether info modal is open
 * @param props.onOpenInfoModal - Handler for opening info modal
 * @param props.onCloseInfoModal - Handler for closing info modal
 * @param props.metricDescriptions - Metric descriptions for info modal
 * @param props.onLeftPanelLayout - Layout handler for measuring left panel width
 * @returns VStack containing the analytics layout
 */
const PerformanceAnalyticsView = ({
  isDark,
  isSmallScreen,
  additionalContent,
  mobileDotPlotSize,
  scatterPlotPlayers,
  xMetricWithValue,
  yMetricWithValue,
  renderYMenuTrigger,
  renderXMenuTrigger,
  createYSelectHandler,
  createXSelectHandler,
  xAxisDropdown,
  yAxisDropdown,
  dotPlotTooltipLabelFormatter,
  playerName,
  spiderChartRef,
  dotPlotRef,
  selectedCompareItem,
  selectedTimeframe,
  radarChartContent,
  dotPlotContent,
  metrics,
  distributionComparisonStatus,
  theme,
  availableMetrics,
  onSelectDistributionMetricAtIndex,
  compareCategoryOptions,
  renderCompareMenuTrigger,
  createCompareCategorySelectHandler,
  compareDropdown,
  timeframeOptions,
  renderTimeframeMenuTrigger,
  createTimeframeSelectHandler,
  timeframeDropdown,
  isSubmenuOpen,
  submenuTitle,
  submenuSearchPlaceholder,
  submenuSearchQuery,
  filteredSubmenuItems,
  isSubmenuLoading = false,
  hasMoreSubmenuItems = false,
  isLoadingMoreSubmenuItems = false,
  onLoadMoreSubmenuItems = () => undefined,
  showComparisonDistribution,
  isPositionComparison,
  isPlayerComparison,
  selectedCompareCategoryType,
  comparisonAverages,
  onCloseSubmenu,
  onSelectSubmenuItem,
  onSubmenuSearchChange,
  isInfoModalOpen,
  onOpenInfoModal,
  onCloseInfoModal,
  metricDescriptions,
  onLeftPanelLayout,
}: PerformanceAnalyticsViewProps) => (
  <Stack className="relative w-full gap-4">
    {/* Header */}
    {isSmallScreen
      ? renderMobileHeader({
          theme,
          isDark,
          onOpenInfoModal,
          renderCompareMenuTrigger,
          compareCategoryOptions,
          createCompareCategorySelectHandler,
          selectedCompareCategoryType,
          compareDropdown,
          renderTimeframeMenuTrigger,
          timeframeOptions,
          createTimeframeSelectHandler,
          selectedTimeframe,
          timeframeDropdown,
        })
      : renderDesktopHeader({
          theme,
          isDark,
          onOpenInfoModal,
          renderCompareMenuTrigger,
          compareCategoryOptions,
          createCompareCategorySelectHandler,
          selectedCompareCategoryType,
          compareDropdown,
          renderTimeframeMenuTrigger,
          timeframeOptions,
          createTimeframeSelectHandler,
          selectedTimeframe,
          timeframeDropdown,
        })}

    {/* Content */}
    {isSmallScreen ? (
      <Stack className="w-full gap-3">
        {/* Spider Graph Card — overflow-visible so selector menus can extend beyond card bounds */}
        <Box
          className={`overflow-visible rounded-md p-3 ${theme.containerClass}`}
        >
          <Stack className="gap-3">
            <Stack
              direction="horizontal"
              className="items-center justify-between"
            >
              <Text variant="title-sm" className={theme.headingClass}>
                Spider Graph
              </Text>
              <ComparisonLegend
                playerName={playerName}
                textClass={theme.textClass}
                isPlayerComparison={isPlayerComparison}
                showComparisonDistribution={showComparisonDistribution}
                isPositionComparison={isPositionComparison}
                selectedCompareItemName={selectedCompareItem?.name}
              />
            </Stack>
            <Box
              ref={spiderChartRef}
              className="w-full items-center justify-center overflow-visible"
            >
              {radarChartContent}
            </Box>
          </Stack>
        </Box>

        {/* Distribution Card — all metrics with compact rows */}
        <Box
          className={`relative overflow-visible rounded-md p-3 ${theme.containerClass}`}
        >
          <Stack className="gap-3">
            <Stack
              direction="horizontal"
              className="items-start justify-between"
            >
              <Text variant="title-sm" className={theme.headingClass}>
                Distribution
              </Text>
              <ComparisonLegend
                playerName={playerName}
                textClass={theme.textClass}
                isPlayerComparison={isPlayerComparison}
                showComparisonDistribution={showComparisonDistribution}
                isPositionComparison={isPositionComparison}
                selectedCompareItemName={selectedCompareItem?.name}
              />
            </Stack>
            {renderDistributionComparisonStatus(distributionComparisonStatus)}
            <View
              className="gap-3 overflow-hidden"
              removeClippedSubviews={Platform.OS === "android"}
            >
              {metrics.map((metric, index) => (
                <View key={index} className="overflow-hidden">
                  <MetricDistributionRow
                    metric={metric}
                    isDark={isDark}
                    index={index}
                    availableMetrics={availableMetrics}
                    onSelectMetricAtIndex={onSelectDistributionMetricAtIndex}
                    comparisonValue={
                      isPlayerComparison
                        ? (comparisonAverages[metric.label] ?? null)
                        : null
                    }
                    compact
                  />
                </View>
              ))}
            </View>
          </Stack>
        </Box>

        {/* Dot Plot Card — rendered directly to avoid Victory SVG painting issues with pre-memoized ReactNodes */}
        <Box
          className={`overflow-visible rounded-md p-3 ${theme.containerClass}`}
        >
          <Stack className="gap-3 overflow-visible">
            <Text variant="title-sm" className={theme.headingClass}>
              Dot Plot
            </Text>
            <DotPlot
              players={scatterPlotPlayers}
              xMetric={xMetricWithValue}
              yMetric={yMetricWithValue}
              availableMetrics={availableMetrics}
              isDark={isDark}
              size={mobileDotPlotSize}
              renderYMenuTrigger={renderYMenuTrigger}
              renderXMenuTrigger={renderXMenuTrigger}
              createYSelectHandler={createYSelectHandler}
              createXSelectHandler={createXSelectHandler}
              xAxisDropdown={xAxisDropdown}
              yAxisDropdown={yAxisDropdown}
              tooltipLabelFormatter={dotPlotTooltipLabelFormatter}
            />
          </Stack>
        </Box>

        {/* Metric Insights below the stacked cards */}
        {additionalContent}
      </Stack>
    ) : (
      <Stack className="w-full gap-4">
        {/* Top row — Spider Graph + Distribution (independent height) */}
        <Box className="grid grid-cols-2 gap-4">
          {/* Spider Graph. View wrapper provides onLayout for
              responsive chart sizing since Box resolves to div on web. */}
          <View className="h-full min-w-0" onLayout={onLeftPanelLayout}>
            <Box
              className={`h-full overflow-hidden rounded-md p-4 ${theme.containerClass}`}
            >
              <Stack className="h-full gap-4">
                <Stack
                  direction="horizontal"
                  className="items-center justify-between"
                >
                  <Text variant="title" className={theme.headingClass}>
                    Spider Graph
                  </Text>
                  <ComparisonLegend
                    playerName={playerName}
                    textClass={theme.textClass}
                    isPlayerComparison={isPlayerComparison}
                    showComparisonDistribution={showComparisonDistribution}
                    isPositionComparison={isPositionComparison}
                    selectedCompareItemName={selectedCompareItem?.name}
                  />
                </Stack>
                <Box
                  ref={spiderChartRef}
                  className="flex-1 items-center justify-center overflow-hidden"
                >
                  {radarChartContent}
                </Box>
              </Stack>
            </Box>
          </View>

          {/* Distribution */}
          <Box
            className={`relative h-full overflow-visible rounded-md p-4 ${theme.containerClass}`}
          >
            <Stack className="h-full gap-4">
              <Stack
                direction="horizontal"
                className="items-start justify-between"
              >
                <Text variant="title" className={theme.headingClass}>
                  Distribution
                </Text>
                <ComparisonLegend
                  playerName={playerName}
                  textClass={theme.textClass}
                  isPlayerComparison={isPlayerComparison}
                  showComparisonDistribution={showComparisonDistribution}
                  isPositionComparison={isPositionComparison}
                  selectedCompareItemName={selectedCompareItem?.name}
                />
              </Stack>
              {renderDistributionComparisonStatus(distributionComparisonStatus)}
              <Stack className="flex-1 gap-2">
                {metrics.map((metric, index) => (
                  <MetricDistributionRow
                    key={metric.label}
                    metric={metric}
                    isDark={isDark}
                    index={index}
                    availableMetrics={availableMetrics}
                    onSelectMetricAtIndex={onSelectDistributionMetricAtIndex}
                    compact
                    comparisonValue={
                      isPlayerComparison
                        ? (comparisonAverages[metric.label] ?? null)
                        : null
                    }
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
        </Box>

        {/* Bottom row — Dot Plot + Metric Insights (independent height) */}
        <Box className="grid grid-cols-2 gap-4">
          {/* Dot Plot — overflow-visible so GlueStack Menu popups extend beyond card bounds */}
          <Box
            className={`h-full overflow-visible rounded-md p-4 ${theme.containerClass}`}
          >
            <Stack className="h-full gap-4">
              <Text variant="title" className={theme.headingClass}>
                Dot Plot
              </Text>
              <Box
                ref={dotPlotRef}
                className="flex-1 items-center justify-center"
              >
                {dotPlotContent}
              </Box>
            </Stack>
          </Box>

          {/* Metric Insights */}
          <View className="min-w-0">{additionalContent}</View>
        </Box>
      </Stack>
    )}

    {/* Compare Submenu Modal - full-screen on mobile, centered modal on desktop */}
    {isSmallScreen
      ? renderMobileSubmenu({
          isDark,
          isSubmenuOpen,
          submenuTitle,
          submenuSearchPlaceholder,
          submenuSearchQuery,
          filteredSubmenuItems,
          isSubmenuLoading,
          hasMoreSubmenuItems,
          isLoadingMoreSubmenuItems,
          onLoadMoreSubmenuItems,
          onCloseSubmenu,
          onSelectSubmenuItem,
          onSubmenuSearchChange,
        })
      : renderDesktopSubmenu({
          isDark,
          isSubmenuOpen,
          submenuTitle,
          submenuSearchPlaceholder,
          submenuSearchQuery,
          filteredSubmenuItems,
          isSubmenuLoading,
          hasMoreSubmenuItems,
          isLoadingMoreSubmenuItems,
          onLoadMoreSubmenuItems,
          onCloseSubmenu,
          onSelectSubmenuItem,
          onSubmenuSearchChange,
        })}

    {/* Metric Descriptions Info Modal */}
    <ScopedModal
      isOpen={isInfoModalOpen}
      onClose={onCloseInfoModal}
      title="Metric Descriptions"
      isDark={isDark}
      maxWidth={600}
      maxHeightPercent={85}
    >
      <Stack className="gap-6">
        {metricDescriptions.map(metric => (
          <Stack key={metric.label} className="gap-2">
            <Text
              variant="title-sm"
              className={isDark ? "text-white" : "text-content-primary"}
            >
              {metric.label}
            </Text>
            <Text
              variant="body"
              className="leading-relaxed text-content-secondary"
            >
              {metric.description}
            </Text>
          </Stack>
        ))}
      </Stack>
    </ScopedModal>
  </Stack>
);

PerformanceAnalyticsView.displayName = "PerformanceAnalyticsView";

export default React.memo(PerformanceAnalyticsView);
